import { useScrollReveal } from '../hooks/useScrollReveal';

const About = () => {
  const ref = useScrollReveal();

  return (
    <div ref={ref} className="min-h-screen bg-offwhite">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px] bg-offblack overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-b from-offblack/40 via-transparent to-offblack/80" />
        <div className="relative z-10 text-center px-6">
          <p className="text-xs tracking-[0.3em] uppercase text-sand mb-4 reveal">Our Story</p>
          <h1 className="font-display text-display-xl text-offwhite mb-6 reveal">
            Crafted for Those<br className="hidden md:block" /> Who Move with Purpose
          </h1>
          <div className="w-12 h-0.5 bg-sand mx-auto reveal" />
        </div>
      </section>

      {/* Origin Story */}
      <section className="section">
        <div className="section-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="reveal">
              <div className="aspect-editorial bg-warmgray-200 overflow-hidden">
                <div className="w-full h-full bg-gradient-to-br from-warmgray-200 to-warmgray-300 flex items-center justify-center">
                  <span className="font-display text-6xl text-warmgray-400">A.</span>
                </div>
              </div>
            </div>
            <div className="space-y-6 reveal">
              <p className="text-xs tracking-[0.2em] uppercase text-sand font-medium">The Beginning</p>
              <h2 className="font-display text-display-md text-offblack">
                Born from a Belief in Better
              </h2>
              <div className="space-y-4 text-warmgray-600 leading-relaxed">
                <p>
                  armé was founded with a singular vision: to create premium essentials that 
                  bridge the gap between streetwear culture and refined craftsmanship. We believe 
                  that what you wear should be an extension of who you are — bold, intentional, 
                  and unapologetically authentic.
                </p>
                <p>
                  Based in Algeria, we source the finest materials to craft jeans and shoes that 
                  stand the test of time. Every stitch, every cut, every detail is considered. 
                  Because great style isn't about following trends — it's about setting the standard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section bg-offblack text-offwhite">
        <div className="section-wide">
          <div className="text-center mb-16 reveal">
            <p className="text-xs tracking-[0.3em] uppercase text-sand mb-4">What We Stand For</p>
            <h2 className="font-display text-display-lg text-offwhite">Our Values</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {/* Craftsmanship */}
            <div className="text-center space-y-4 reveal">
              <div className="w-16 h-16 border border-sand/30 flex items-center justify-center mx-auto mb-6">
                <svg className="w-7 h-7 text-sand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                </svg>
              </div>
              <h3 className="font-display text-xl text-offwhite">Craftsmanship</h3>
              <p className="text-warmgray-400 text-sm leading-relaxed max-w-xs mx-auto">
                Every piece is crafted with meticulous attention to detail. We obsess over 
                the details so you don't have to — from reinforced seams to precision-cut patterns.
              </p>
            </div>

            {/* Quality Materials */}
            <div className="text-center space-y-4 reveal">
              <div className="w-16 h-16 border border-sand/30 flex items-center justify-center mx-auto mb-6">
                <svg className="w-7 h-7 text-sand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                </svg>
              </div>
              <h3 className="font-display text-xl text-offwhite">Quality Materials</h3>
              <p className="text-warmgray-400 text-sm leading-relaxed max-w-xs mx-auto">
                We source premium fabrics and materials built to endure. Our jeans get 
                better with every wear. Our shoes are built to go the distance.
              </p>
            </div>

            {/* Sustainability */}
            <div className="text-center space-y-4 reveal">
              <div className="w-16 h-16 border border-sand/30 flex items-center justify-center mx-auto mb-6">
                <svg className="w-7 h-7 text-sand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12.75 3.03v.568c0 .334.148.65.405.864l1.068.89c.442.369.535 1.01.216 1.49l-.51.766a2.25 2.25 0 01-1.161.886l-.143.048a1.107 1.107 0 00-.57 1.664c.369.555.169 1.307-.427 1.605L9 13.125l.423 1.059a.956.956 0 01-1.652.928l-.679-.906a1.125 1.125 0 00-1.906.172L4.5 15.75l-.612.153M12.75 3.031a9 9 0 00-8.862 12.872M12.75 3.031a9 9 0 016.69 14.036m0 0l-.177-.529A2.25 2.25 0 0017.128 15H16.5l-.324-.324a1.453 1.453 0 00-2.328.377l-.036.073a1.586 1.586 0 01-.982.816l-.99.282c-.55.157-.894.702-.8 1.267l.073.438c.08.474.49.821.97.821.846 0 1.598.542 1.865 1.345l.215.643m5.276-3.67a9.012 9.012 0 01-5.276 3.67m0 0a9 9 0 01-10.275-4.835M15.75 9c0 .896-.393 1.7-1.016 2.25" />
                </svg>
              </div>
              <h3 className="font-display text-xl text-offwhite">Responsible Fashion</h3>
              <p className="text-warmgray-400 text-sm leading-relaxed max-w-xs mx-auto">
                We believe in making less, but making it better. Timeless designs that 
                don't chase seasonal trends mean fewer items in landfills.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Behind the Scenes */}
      <section className="section">
        <div className="section-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-6 reveal order-2 lg:order-1">
              <p className="text-xs tracking-[0.2em] uppercase text-sand font-medium">Behind the Scenes</p>
              <h2 className="font-display text-display-md text-offblack">
                From Workshop to Wardrobe
              </h2>
              <div className="space-y-4 text-warmgray-600 leading-relaxed">
                <p>
                  Our process begins with inspiration — from the streets of Algiers to the fashion 
                  capitals of the world. Each collection is designed to be timeless, blending 
                  modern aesthetics with enduring quality.
                </p>
                <p>
                  We work with skilled artisans who share our passion for perfection. Every pair 
                  of jeans is washed and finished to achieve the perfect drape. Every shoe is 
                  assembled by hand with care that machines cannot replicate.
                </p>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div>
                  <p className="font-display text-3xl text-offblack">500+</p>
                  <p className="text-xs tracking-wider uppercase text-warmgray-500">Products Crafted</p>
                </div>
                <div className="w-px h-12 bg-warmgray-200" />
                <div>
                  <p className="font-display text-3xl text-offblack">58</p>
                  <p className="text-xs tracking-wider uppercase text-warmgray-500">Wilayas Served</p>
                </div>
                <div className="w-px h-12 bg-warmgray-200" />
                <div>
                  <p className="font-display text-3xl text-offblack">2k+</p>
                  <p className="text-xs tracking-wider uppercase text-warmgray-500">Happy Customers</p>
                </div>
              </div>
            </div>
            <div className="reveal order-1 lg:order-2">
              <div className="aspect-editorial bg-warmgray-200 overflow-hidden">
                <div className="w-full h-full bg-gradient-to-br from-warmgray-200 to-warmgray-300 flex items-center justify-center">
                  <span className="font-display text-6xl text-warmgray-400">M.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 md:py-28 bg-warmgray-200">
        <div className="section-wide text-center reveal">
          <p className="text-xs tracking-[0.3em] uppercase text-sand mb-4">Ready to Explore?</p>
          <h2 className="font-display text-display-lg text-offblack mb-8">
            Discover the Collection
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="/category/jeans" className="btn-primary btn-lg">
              Shop Jeans
            </a>
            <a href="/category/shoes" className="btn-secondary btn-lg">
              Shop Shoes
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
